import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const storageKey = 'gnome:rituals-cart-offer:v1';
const stateEvent = 'gnome:rituals-offer-state';
let saved = '';
try { saved = sessionStorage.getItem(storageKey) || ''; } catch {}
const state = { dismissed: saved === 'dismissed', pending: saved === 'pending', busy: false, error: '' };

export function hasRituals(cart) {
  const visit = (lines) => (lines || []).some((line) => {
    const id = String(line.product_id ?? line.product?.id ?? '');
    return id === '9338782548106' || id === '9394281578634' || visit(line.item_components);
  });
  return visit(cart?.items);
}

function persist(value) {
  try { sessionStorage.setItem(storageKey, value); } catch {}
}

function syncAll() {
  document.dispatchEvent(new Event(stateEvent));
}

async function pendingKey(cart) {
  if (!cart.token || !window.crypto?.subtle) return storageKey + ':shared-pending';
  const bytes = new TextEncoder().encode(cart.token);
  const hash = await window.crypto.subtle.digest('SHA-256', bytes);
  return storageKey + ':pending:' + [...new Uint8Array(hash)].map((n) => n.toString(16).padStart(2, '0')).join('');
}

function sharedPending(key, value) {
  try {
    if (value === true) localStorage.setItem(key, 'pending');
    else if (value === false) localStorage.removeItem(key);
    else return localStorage.getItem(key) === 'pending';
  } catch {}
  return false;
}

function dismiss() {
  state.dismissed = true;
  state.pending = false;
  persist('dismissed');
  syncAll();
}

async function readCart() {
  const response = await fetch(window.Shopify.routes.root + 'cart.js', { credentials: 'same-origin', cache: 'no-store' });
  if (!response.ok) throw new Error('Cart could not be read');
  const cart = await response.json();
  if (!Array.isArray(cart.items)) throw new Error('Cart response was incomplete');
  return cart;
}

function publishCart(cart, sections, action = 'add') {
  const update = CartLinesUpdateEvent.createPromise();
  document.dispatchEvent(new CartLinesUpdateEvent({
    action,
    context: 'cart',
    lines: [],
    promise: update.promise,
  }));
  update.resolve({
    cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
    detail: { sections, items: cart.items, itemCount: cart.item_count, source: 'gnome-rituals-cart-offer', didError: false },
  });
}

function announce(text) {
  let region = document.querySelector('[data-gn-rituals-announcement]');
  if (!region) {
    region = document.createElement('div');
    region.className = 'visually-hidden';
    region.dataset.gnRitualsAnnouncement = '';
    region.setAttribute('role', 'status');
    region.setAttribute('aria-live', 'polite');
    document.body.append(region);
  }
  region.textContent = text;
}

class GnomeRitualsCartOffer extends HTMLElement {
  static observedAttributes = ['hidden'];
  #connected = false;

  connectedCallback() {
    this.#connected = true;
    this.addEventListener('click', this.#onClick);
    document.addEventListener(stateEvent, this.#sync);
    this.#sync();
  }

  disconnectedCallback() {
    this.#connected = false;
    this.removeEventListener('click', this.#onClick);
    document.removeEventListener(stateEvent, this.#sync);
  }

  attributeChangedCallback() {
    if (this.#connected && state.dismissed && !this.hidden) this.hidden = true;
  }

  #sync = () => {
    this.hidden = state.dismissed;
    this.setAttribute('aria-busy', String(state.busy));
    const button = this.querySelector('[data-gn-add]');
    const select = this.querySelector('[data-gn-variant]');
    if (button) {
      button.disabled = state.busy || state.pending;
      button.textContent = state.busy ? this.dataset.addingLabel : this.dataset.addLabel;
    }
    if (select) select.disabled = state.busy || state.pending;
    if (state.pending && !state.busy && !state.dismissed) this.#error(this.dataset.uncertainLabel, true);
    else if (state.error) this.#error(state.error);
  };

  #error(message, checkCart = false) {
    const error = this.querySelector('[data-gn-error]');
    if (error) {
      error.textContent = message;
      error.hidden = !message;
    }
    const link = this.querySelector('[data-gn-check]');
    if (link) link.hidden = !checkCart;
  }

  #focusCheckout() {
    requestAnimationFrame(() => requestAnimationFrame(() => {
      const active = document.activeElement;
      if (active === document.body || this.contains(active)) document.querySelector('#checkout')?.focus({ preventScroll: true });
    }));
  }

  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (target?.closest('[data-gn-dismiss]')) {
      dismiss();
      this.#focusCheckout();
    } else if (target?.closest('[data-gn-add]')) {
      void this.#add();
    }
  };

  async #add() {
    if (state.busy || state.pending || state.dismissed) return;
    const variantId = this.querySelector('[data-gn-variant]')?.value;
    if (!variantId || !/^\d+$/.test(variantId)) return;
    state.busy = true;
    state.error = '';
    this.#error('');
    syncAll();
    const run = async () => {
      let attempted = false;
      let definiteFailure = false;
      let sharedKey = '';
      try {
        const before = await readCart();
        sharedKey = await pendingKey(before);
        if (hasRituals(before)) {
          sharedPending(sharedKey, false);
          dismiss();
          publishCart(before, undefined, 'update');
          this.#focusCheckout();
          return;
        }
        if (sharedPending(sharedKey)) {
          state.pending = true;
          persist('pending');
          return;
        }
        if (!before.items.length) {
          this.remove();
          publishCart(before, undefined, 'update');
          return;
        }
        const sections = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')].map((item) => item.dataset.sectionId))].slice(0, 5);
        state.pending = true;
        persist('pending');
        sharedPending(sharedKey, true);
        attempted = true;
        const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }], sections, sections_url: window.location.pathname + window.location.search }),
        });
        definiteFailure = response.status >= 400 && response.status < 500;
        const added = await response.json();
        if (!response.ok || added.status) throw new Error('Add was not confirmed');
        let cart;
        try { cart = await readCart(); } catch { cart = await readCart(); }
        dismiss();
        sharedPending(sharedKey, false);
        publishCart(cart, added.sections);
        announce(this.dataset.addedLabel);
        this.#focusCheckout();
      } catch {
        if (attempted) {
          try {
            const cart = await readCart();
            if (hasRituals(cart)) {
              sharedPending(sharedKey, false);
              dismiss();
              publishCart(cart);
              announce(this.dataset.addedLabel);
              this.#focusCheckout();
              return;
            }
            if (definiteFailure) {
              sharedPending(sharedKey, false);
              state.pending = false;
              persist('');
              publishCart(cart, undefined, 'update');
            }
          } catch {}
        }
        state.error = state.pending ? this.dataset.uncertainLabel : this.dataset.failedLabel;
        this.#error(state.error, state.pending);
      }
    };
    try {
      if (navigator.locks?.request) await navigator.locks.request('gnome-rituals-cart-add', run);
      else await run();
    } finally {
      state.busy = false;
      syncAll();
    }
  }
}

if (!customElements.get('gn-rituals-cart-offer')) customElements.define('gn-rituals-cart-offer', GnomeRitualsCartOffer);
document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  event.promise?.then(() => requestAnimationFrame(() => requestAnimationFrame(syncAll))).catch(() => {});
});
document.addEventListener('cart-section:restored', syncAll);
window.addEventListener('pageshow', () => {
  try {
    const value = sessionStorage.getItem(storageKey);
    state.dismissed = value === 'dismissed';
    state.pending = value === 'pending';
  } catch {}
  syncAll();
});
